import { useState } from "react";
import { Plus, Trash2, GripVertical, ChevronUp, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SAKRAMENT_LABELS } from "@/lib/constants";

export const FIELD_TYPES = [
  { value: "checkbox", label: "Kontrollkästchen (Mehrfachauswahl)" },
  { value: "radio", label: "Optionsschaltfläche" },
  { value: "select", label: "Aufklappmenü" },
  { value: "file", label: "Datei (hochladen)" },
  { value: "short", label: "Kurze Antwort" },
  { value: "long", label: "Langer Text" },
  { value: "date", label: "Datum" },
  { value: "time", label: "Zeit" },
  { value: "heading", label: "Überschrift (fett)" },
  { value: "datenschutz", label: "Datenschutzerklärung" },
  { value: "pagebreak", label: "Seitenumbruch" },
];

const STRUCT = ["heading", "pagebreak", "datenschutz"];

const HAS_OPTIONS = ["checkbox", "radio", "select"];
const uid = () => Math.random().toString(36).slice(2, 10);

export default function FormBuilder({ fields, onChange, dsTemplates = [], sakrament = "", allowDatenschutz = true }) {
  const [items, setItems] = useState(fields || []);
  const [dragIdx, setDragIdx] = useState(null);

  const sync = (next) => { setItems(next); onChange(next); };

  const move = (i, j) => {
    if (j < 0 || j >= items.length) return;
    const n = [...items];
    [n[i], n[j]] = [n[j], n[i]];
    sync(n);
  };
  const dropOn = (i) => {
    if (dragIdx === null || dragIdx === i) return setDragIdx(null);
    const n = [...items];
    const [m] = n.splice(dragIdx, 1);
    n.splice(i, 0, m);
    sync(n);
    setDragIdx(null);
  };

  const add = () => sync([...items, { id: uid(), type: "short", label: "", required: false, options: [] }]);
  const update = (id, patch) => sync(items.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  const remove = (id) => sync(items.filter((f) => f.id !== id));
  const addOption = (id) => update(id, { options: [...(items.find((f) => f.id === id).options || []), ""] });
  const setOption = (id, i, v) => {
    const f = items.find((x) => x.id === id);
    const opts = [...f.options]; opts[i] = v;
    update(id, { options: opts });
  };
  const removeOption = (id, i) => {
    const f = items.find((x) => x.id === id);
    update(id, { options: f.options.filter((_, idx) => idx !== i) });
  };

  return (
    <div className="space-y-4" data-testid="form-builder">
      {items.map((f, idx) => (
        <div
          key={f.id}
          onDragOver={(e) => e.preventDefault()}
          onDrop={() => dropOn(idx)}
          className={`rounded-lg p-5 shadow-sm ${f.type === "pagebreak" ? "bg-amber-100 border-2 border-dashed border-amber-500" : "bg-white border border-border"} ${dragIdx === idx ? "opacity-50 ring-2 ring-primary" : ""}`}
          data-testid={`builder-field-${idx}`}
        >
          <div className="flex items-start gap-3">
            <div className="flex flex-col items-center mt-1 shrink-0">
              <span draggable onDragStart={() => setDragIdx(idx)} className="cursor-grab active:cursor-grabbing text-slate-400" title="Zum Sortieren ziehen" data-testid={`builder-drag-${idx}`}><GripVertical size={18} /></span>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => move(idx, idx - 1)} disabled={idx === 0} data-testid={`builder-up-${idx}`}><ChevronUp size={14} /></Button>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => move(idx, idx + 1)} disabled={idx === items.length - 1} data-testid={`builder-down-${idx}`}><ChevronDown size={14} /></Button>
            </div>
            <div className="flex-1 space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className={`text-xs ${f.type === "pagebreak" ? "text-amber-800 font-bold uppercase tracking-wide" : ""}`}>{f.type === "heading" ? "Überschrift-Text" : f.type === "pagebreak" ? "⎯⎯ Seitenumbruch ⎯⎯" : f.type === "datenschutz" ? "Datenschutzerklärung (Vorlage)" : "Feldbezeichnung"}</Label>
                  {f.type === "pagebreak"
                    ? <p className="text-sm text-amber-800 font-medium py-2">Hier beginnt im Formular eine neue Seite (mit „Weiter"/„Zurück").</p>
                    : f.type === "datenschutz"
                    ? <Select value={f.template_id || ""} onValueChange={(v) => update(f.id, { template_id: v })}>
                        <SelectTrigger data-testid={`builder-ds-template-${idx}`}><SelectValue placeholder="Vorlage wählen" /></SelectTrigger>
                        <SelectContent>
                          {dsTemplates.length === 0 && <div className="px-2 py-1.5 text-xs text-muted-foreground">Keine Vorlagen – unter „Verwaltung › Datenschutzerklärungen" anlegen.</div>}
                          {dsTemplates.map((t) => <SelectItem key={t.id} value={t.id}>{(SAKRAMENT_LABELS[t.sakrament] || t.sakrament)} – {t.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    : <Input value={f.label} onChange={(e) => update(f.id, { label: e.target.value })} placeholder={f.type === "heading" ? "z.B. Angaben zum Vater" : "z.B. Vorname des Kindes"} className="font-bold text-base" data-testid={`builder-label-${idx}`} />}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Feldtyp</Label>
                  <Select value={f.type} onValueChange={(v) => update(f.id, { type: v, options: HAS_OPTIONS.includes(v) ? (f.options?.length ? f.options : [""]) : [], ...(v === "datenschutz" && !f.template_id ? { template_id: (dsTemplates.find((t) => t.sakrament === sakrament) || dsTemplates[0] || {}).id } : {}) })}>
                    <SelectTrigger data-testid={`builder-type-${idx}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{(allowDatenschutz ? FIELD_TYPES : FIELD_TYPES.filter((t) => t.value !== "datenschutz")).map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              {HAS_OPTIONS.includes(f.type) && (
                <div className="space-y-2 pl-1">
                  <Label className="text-xs">Auswahlmöglichkeiten</Label>
                  {(f.options || []).map((o, i) => (
                    <div key={i} className="flex gap-2">
                      <Input value={o} onChange={(e) => setOption(f.id, i, e.target.value)} placeholder={`Option ${i + 1}`} data-testid={`builder-option-${idx}-${i}`} />
                      <Button variant="ghost" size="sm" onClick={() => removeOption(f.id, i)}><Trash2 size={14} /></Button>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={() => addOption(f.id)}><Plus size={14} className="mr-1" /> Option</Button>
                  {f.type === "select" && (
                    <label className="flex items-center gap-2 cursor-pointer pt-1">
                      <Checkbox checked={!!f.allow_other} onCheckedChange={(v) => update(f.id, { allow_other: !!v })} data-testid={`builder-allow-other-${idx}`} />
                      <span className="text-sm">Option „Sonstiges" mit Freieingabe anbieten</span>
                    </label>
                  )}
                </div>
              )}

              {!STRUCT.includes(f.type) && (
                <>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={f.required} onCheckedChange={(v) => update(f.id, { required: !!v })} data-testid={`builder-required-${idx}`} />
                    <span className="text-sm">Pflichtfeld</span>
                  </label>

                  <div className="space-y-1.5">
                    <Label className="text-xs">Bemerkung / Erläuterung (optional, wird den Eltern angezeigt)</Label>
                    <Input value={f.note || ""} onChange={(e) => update(f.id, { note: e.target.value })} placeholder="z.B. Bitte Geburtsurkunde bereithalten" data-testid={`builder-note-${idx}`} />
                  </div>
                </>
              )}

              {f.type !== "pagebreak" && (() => {
                    const conds = f.visible_conditions || (f.visible_if ? [f.visible_if] : []);
                    const controllers = items.filter((x, i) => i < idx && ["radio", "select"].includes(x.type) && x.label);
                    const save = (next) => update(f.id, { visible_conditions: next, visible_if: null });
                    return (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="text-xs">Sichtbarkeit (abhängig von Auswahlfeldern)</Label>
                          {conds.length > 1 && (
                            <Select value={f.visible_mode || "and"} onValueChange={(v) => update(f.id, { visible_mode: v })}>
                              <SelectTrigger className="h-7 w-44 text-xs" data-testid={`builder-visif-mode-${idx}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="and">Alle Bedingungen (UND)</SelectItem>
                                <SelectItem value="or">Eine Bedingung (ODER)</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                        {conds.length === 0 && <p className="text-xs text-muted-foreground">Wird immer angezeigt.</p>}
                        {conds.map((cond, ci) => {
                          const ctrl = items.find((x) => x.id === cond.field);
                          return (
                            <div key={ci} className="flex gap-2 items-center">
                              <Select value={cond.field || ""} onValueChange={(v) => save(conds.map((c, i) => i === ci ? { field: v, value: "" } : c))}>
                                <SelectTrigger className="flex-1" data-testid={`builder-visif-field-${idx}-${ci}`}><SelectValue placeholder="Feld wählen" /></SelectTrigger>
                                <SelectContent>{controllers.map((cf) => <SelectItem key={cf.id} value={cf.id}>{cf.label}</SelectItem>)}</SelectContent>
                              </Select>
                              <span className="text-xs text-muted-foreground">=</span>
                              <Select value={cond.value || ""} onValueChange={(v) => save(conds.map((c, i) => i === ci ? { ...c, value: v } : c))}>
                                <SelectTrigger className="flex-1" data-testid={`builder-visif-value-${idx}-${ci}`}><SelectValue placeholder="Wert" /></SelectTrigger>
                                <SelectContent>{(ctrl?.options || []).filter(Boolean).map((o, i) => <SelectItem key={i} value={o}>{o}</SelectItem>)}</SelectContent>
                              </Select>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => save(conds.filter((_, i) => i !== ci))} data-testid={`builder-visif-remove-${idx}-${ci}`}><Trash2 size={14} /></Button>
                            </div>
                          );
                        })}
                        {controllers.length > 0 && (
                          <Button variant="outline" size="sm" onClick={() => save([...conds, { field: "", value: "" }])} data-testid={`builder-visif-add-${idx}`}><Plus size={14} className="mr-1" /> Sichtbarkeits-Bedingung</Button>
                        )}
                      </div>
                    );
                  })()}
            </div>
            <Button variant="ghost" size="sm" onClick={() => remove(f.id)} data-testid={`builder-remove-${idx}`}><Trash2 size={16} className="text-destructive" /></Button>
          </div>
        </div>
      ))}
      <Button variant="outline" onClick={add} data-testid="builder-add-field"><Plus size={16} className="mr-1" /> Feld hinzufügen</Button>
    </div>
  );
}
